// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/** English UI strings: Workspace administration (routes/WorkspacePage.tsx). Merged into the catalog by messages.en.ts. */
export const workspaceAdmin = {
  "workspaceAdmin.sectionsNav": "Workspace sections",
  "workspaceAdmin.general": "General",
  "workspaceAdmin.group.workspace": "Workspace",
  "workspaceAdmin.group.people": "People",
  "workspaceAdmin.group.security": "Security",
  "workspaceAdmin.yourRole": "your role: {role}",
  "workspaceAdmin.loading": "Loading",
  "workspaceAdmin.save": "Save",
  "workspaceAdmin.saved": "Saved",
  "workspaceAdmin.cancel": "Cancel",
  "workspaceAdmin.remove": "Remove",
  "workspaceAdmin.turnOff": "Turn off",

  // Role names, as shown in lists and role pickers.
  "workspaceAdmin.role.owner": "owner",
  "workspaceAdmin.role.admin": "admin",
  "workspaceAdmin.role.member": "member",
  "workspaceAdmin.role.guest": "guest",

  "workspaceAdmin.policyBlock.default": "This workspace's security policy blocks access from here.",
  "workspaceAdmin.policyBlock.twoFactorRequired": "This workspace requires two-step verification. Turn it on in Settings to open its vaults.",
  "workspaceAdmin.policyBlock.sessionExpired": "This workspace limits how long you stay signed in. Sign out and sign in again to open its vaults.",
  "workspaceAdmin.policyBlock.clientNotAllowed": "This workspace does not allow access from this app.",
  "workspaceAdmin.policyBlock.ssoRequired": "This workspace requires single sign-on. Sign out, then choose “Sign in with SSO”.",

  "workspaceAdmin.members.title": "Members",
  "workspaceAdmin.members.description": "Owners and admins manage the workspace; members use shared vaults; guests can only view and comment.",
  "workspaceAdmin.members.roleOf": "Role of {email}",
  "workspaceAdmin.members.adminDescription":
    "Everyone in the workspace. Owners and admins manage it; members use shared vaults; guests can only view and comment. Storage counts what each person last saved in the workspace's vaults plus their personal vaults.",
  "workspaceAdmin.members.lastSignIn": "Last sign-in {date}",
  "workspaceAdmin.members.neverSignedIn": "Not signed in yet",
  "workspaceAdmin.members.storage": "{size} used",
  "workspaceAdmin.members.twoFactorOn": "Two-step on",
  "workspaceAdmin.members.twoFactorOff": "Two-step off",

  "workspaceAdmin.deleteAccount.open": "Delete account",
  "workspaceAdmin.deleteAccount.title": "Delete {who}'s account",
  "workspaceAdmin.deleteAccount.description":
    "The account belongs to your organization. It is signed out everywhere and can't be restored. Notes they saved in shared vaults stay with the workspace.",
  "workspaceAdmin.deleteAccount.form": "Delete account",
  "workspaceAdmin.deleteAccount.their": "{who}'s personal folders and notes",
  "workspaceAdmin.deleteAccount.transfer": "Give them to",
  "workspaceAdmin.deleteAccount.transferTo": "Member who receives the folders and notes",
  "workspaceAdmin.deleteAccount.choose": "Choose a member…",
  "workspaceAdmin.deleteAccount.purge": "Delete them with the account",
  "workspaceAdmin.deleteAccount.download": "Download as ZIP",
  "workspaceAdmin.deleteAccount.downloadHint": "Keep a copy before deleting.",
  "workspaceAdmin.deleteAccount.submit": "Delete account",
  "workspaceAdmin.deleteAccount.done": "{who}'s account is deleted.",
  "workspaceAdmin.deleteAccount.doneMoved": "{who}'s account is deleted. Their vaults moved: {vaults}.",
  "workspaceAdmin.deleteAccount.close": "Close",

  "workspaceAdmin.invitations.title": "Invitations",
  "workspaceAdmin.invitations.description":
    "Invite anyone by email, at any domain (the link works once, for that address) or share a link (anyone signed in can use it until it expires).",
  "workspaceAdmin.invitations.shareLink": "Share this link:",
  "workspaceAdmin.invitations.email": "Email",
  "workspaceAdmin.invitations.role": "Role",
  "workspaceAdmin.invitations.send": "Send invitation",
  "workspaceAdmin.invitations.createLink": "Create link",
  "workspaceAdmin.invitations.pending": "Pending invitations",
  "workspaceAdmin.invitations.link": "Link",
  "workspaceAdmin.invitations.item": "{who} · {role} · until {date}",
  "workspaceAdmin.invitations.revoke": "Revoke",

  "workspaceAdmin.domains.title": "Email domains",
  "workspaceAdmin.domains.description": "Prove your company owns its email domain, for single sign-on. People still join only by invitation.",
  "workspaceAdmin.domains.list": "Domains",
  "workspaceAdmin.domains.verified": "Verified",
  "workspaceAdmin.domains.verify": "Verify",
  "workspaceAdmin.domains.txtRecord": "Add this TXT record to {domain}:",
  "workspaceAdmin.domains.domain": "Domain",
  "workspaceAdmin.domains.domainPlaceholder": "example.com",
  "workspaceAdmin.domains.add": "Add domain",

  "workspaceAdmin.sso.title": "Single sign-on",
  "workspaceAdmin.sso.description":
    "Let people at your verified domains sign in with your identity provider: OpenID Connect (Google Workspace, Okta, Microsoft Entra ID, Keycloak) or SAML 2.0.",
  "workspaceAdmin.sso.serviceProvider": "Service provider",
  "workspaceAdmin.sso.register": "Register Granite with your identity provider:",
  "workspaceAdmin.sso.entityId": "Entity ID / metadata URL:",
  "workspaceAdmin.sso.acs": "Assertion consumer service (HTTP-POST):",
  "workspaceAdmin.sso.redirectUri": "Redirect URI to register with the provider:",
  "workspaceAdmin.sso.protocol": "Protocol",
  "workspaceAdmin.sso.oidc": "OpenID Connect",
  "workspaceAdmin.sso.saml": "SAML 2.0",
  "workspaceAdmin.sso.issuerUrl": "Issuer URL",
  "workspaceAdmin.sso.issuerPlaceholder": "https://accounts.example.com",
  "workspaceAdmin.sso.clientId": "Client ID",
  "workspaceAdmin.sso.clientSecret": "Client secret",
  "workspaceAdmin.sso.secretSaved": "Saved; type to replace",
  "workspaceAdmin.sso.identityProvider": "Identity provider: {issuer}",
  "workspaceAdmin.sso.metadataUrl": "Identity provider metadata URL",
  "workspaceAdmin.sso.metadataUrlPlaceholder": "https://idp.example.com/saml/metadata",
  "workspaceAdmin.sso.metadataXml": "Or paste the metadata XML",
  "workspaceAdmin.sso.inviteOnly": "Only members and people you invited by email can sign in with SSO. Invite new colleagues first; their invitation sets their role.",
  "workspaceAdmin.sso.enforce": "Require SSO for everyone at the verified domains (the owner can still use a password)",
  "workspaceAdmin.sso.lastSignIn": "Last SSO sign-in: {time}.",
  "workspaceAdmin.sso.testFirst": "Sign in with SSO once to check the setup before requiring it.",
  "workspaceAdmin.sso.connect": "Connect",
  "workspaceAdmin.sso.remove": "Remove SSO",

  "workspaceAdmin.scim.title": "Provisioning (SCIM)",
  "workspaceAdmin.scim.description":
    "Let your identity provider add, update, and remove members and groups. Removing or deactivating someone there signs them out of Granite everywhere at once.",
  "workspaceAdmin.scim.baseUrl": "SCIM base URL:",
  "workspaceAdmin.scim.tokenLastUsed": "Token {prefix}… · last used {time}",
  "workspaceAdmin.scim.tokenNotUsed": "Token {prefix}… · not used yet",
  "workspaceAdmin.scim.off": "Provisioning is off.",
  "workspaceAdmin.scim.newToken": "New SCIM token",
  "workspaceAdmin.scim.copyToken": "Copy this token into your identity provider now; it is not shown again.",
  "workspaceAdmin.scim.replaceToken": "Replace token",
  "workspaceAdmin.scim.turnOn": "Turn on provisioning",

  "workspaceAdmin.policy.title": "Security policy",
  "workspaceAdmin.policy.description": "Rules for everyone who opens this workspace's vaults, including admins. Changes apply at once.",
  "workspaceAdmin.policy.requireTwoFactor": "Require two-step verification",
  "workspaceAdmin.policy.sessionLength": "Session length",
  "workspaceAdmin.policy.sessionHint": "After this long, people sign in again to open the workspace's vaults.",
  "workspaceAdmin.policy.session.none": "No limit",
  "workspaceAdmin.policy.session.8h": "8 hours",
  "workspaceAdmin.policy.session.1d": "1 day",
  "workspaceAdmin.policy.session.7d": "7 days",
  "workspaceAdmin.policy.session.30d": "30 days",
  "workspaceAdmin.policy.session.hours": "{hours} hours",
  "workspaceAdmin.policy.allowedApps": "Allowed apps",
  "workspaceAdmin.policy.client.web": "Web",
  "workspaceAdmin.policy.client.desktop": "Desktop apps",
  "workspaceAdmin.policy.client.mobile": "Mobile apps",
  "workspaceAdmin.policy.client.api": "API tokens, connected apps, and the command line",
  "workspaceAdmin.policy.mcp": "AI tools (MCP)",
  "workspaceAdmin.policy.mcp.on": "Read and write",
  "workspaceAdmin.policy.mcp.readOnly": "Read only",
  "workspaceAdmin.policy.mcp.off": "Off",
  "workspaceAdmin.policy.knowledgeBase": "Knowledge base (semantic search for people and AI tools)",
  "workspaceAdmin.policy.drive": "Google Drive references",
  "workspaceAdmin.policy.save": "Save policy",

  "workspaceAdmin.manage.name": "Name",
  "workspaceAdmin.manage.rename": "Rename",
  "workspaceAdmin.manage.transferTo": "Transfer ownership to",
  "workspaceAdmin.manage.chooseMember": "Choose a member",
  "workspaceAdmin.manage.transfer": "Transfer",
  "workspaceAdmin.manage.confirmDelete": "Delete {name}? Its shared vaults are deleted too.",
  "workspaceAdmin.manage.delete": "Delete workspace",
  "workspaceAdmin.manage.leave": "Leave workspace",

  "workspaceAdmin.vaults.title": "Shared vaults",
  "workspaceAdmin.vaults.description": "Vaults that belong to the workspace. You see the ones shared with you; owners and admins see all.",
  "workspaceAdmin.vaults.empty": "No shared vaults yet.",
  "workspaceAdmin.vaults.new": "New shared vault",
  "workspaceAdmin.vaults.create": "Create",

  "workspaceAdmin.groups.title": "Teams",
  "workspaceAdmin.groups.description": "Give vaults, folders, and notes to a team instead of person by person. Only owners and admins add and remove team members. Changes apply right away.",
  "workspaceAdmin.groups.memberCount": "{count} members",
  "workspaceAdmin.groups.delete": "Delete",
  "workspaceAdmin.groups.membersOf": "Members of {name}",
  "workspaceAdmin.groups.addTo": "Add to {name}",
  "workspaceAdmin.groups.addMember": "Add a member",
  "workspaceAdmin.groups.add": "Add",
  "workspaceAdmin.groups.new": "New team",
  "workspaceAdmin.groups.create": "Create team",

  "workspaceAdmin.audit.title": "Audit log",
  "workspaceAdmin.audit.description": "Who did what in this workspace: membership, sharing, settings, sign-ins with workspace tools.",
  "workspaceAdmin.audit.show": "Show",
  "workspaceAdmin.audit.filter.all": "Everything",
  "workspaceAdmin.audit.filter.members": "Members",
  "workspaceAdmin.audit.filter.vaults": "Vaults and sharing",
  "workspaceAdmin.audit.filter.groups": "Teams",
  "workspaceAdmin.audit.filter.domains": "Domains",
  "workspaceAdmin.audit.filter.workspace": "Workspace",
  "workspaceAdmin.audit.filter.audit": "Audit",
  "workspaceAdmin.audit.exportCsv": "Export CSV",
  "workspaceAdmin.audit.exportJson": "Export JSON",
  "workspaceAdmin.audit.events": "Audit events",
  "workspaceAdmin.audit.system": "system",
  "workspaceAdmin.audit.empty": "No events.",
  "workspaceAdmin.audit.loadMore": "Load more",
  "workspaceAdmin.audit.streaming": "Streaming",
  "workspaceAdmin.audit.streamingHint": "Send new events to your security tools (an HTTPS endpoint), signed like webhooks, within a minute.",
  "workspaceAdmin.audit.signingSecret": "Signing secret (shown once):",
  "workspaceAdmin.audit.lastError": "· last error: {error}",
  "workspaceAdmin.audit.stop": "Stop",
  "workspaceAdmin.audit.endpointUrl": "Endpoint URL",
  "workspaceAdmin.audit.endpointPlaceholder": "https://siem.example.com/granite",
  "workspaceAdmin.audit.startStreaming": "Start streaming",
} as const;
